import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import {
  userLogin,
  userLoginSuccess,
  userLoginFailure,
  userSendVerification,
  userSendVerificationSuccess,
  userSendVerificationFailure,
  userResetPassword,
  userResetPasswordSuccess,
  userResetPasswordFailure,
  userchangePassword,
  userchangePasswordSuccess,
  userchangePasswordFailure,
  userSignup,
  userSignupSuccess,
  userSignupFailure,
  getUser,
  getUserSuccess,
  getUserFailure,
  addFilter,
  addFilterFailure,
  addFilterSuccess,
  getFilters,
  getFiltersSuccess,
  getFiltersFailure,
  getFilter,
  getFilterSuccess,
  getFilterFailure,
  deleteFilter,
  deleteFilterSuccess,
  deleteFilterFailure,
  updateUserProfile,
  updateUserProfileSuccess,
  updateUserProfileFailure,
  UpdateUser,
  UpdateUserSuccess,
  UpdateUserFailure,
  getUserLoginHistories,
  getUserLoginHistoriesSuccess,
  getUserLoginHistoriesFailure,
  userSendActivation,
  userSendActivationSuccess,
  userSendActivationFailure,
  resendSuccess,
  resendFailure,
  resendVerification,
} from './user.actions';
import { of } from 'rxjs';
import { map, mergeMap, catchError, tap } from 'rxjs/operators';
import { UserService } from '@services/user/user.service';
import { PageService } from '@services/page/page.service';
import {UserRole} from '@enums/EUserRole';

@Injectable()
export class UserEffects {

  loginUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userLogin),
      mergeMap(action =>
        this.userService.login(action.email, action.password).pipe(
          map(res => {
            const roleId = res['user'] && res['user']['roles'][0].role_id;

            if (roleId === UserRole.MANAGER || roleId === UserRole.SUPERADMIN) {
              return userLoginFailure({error: true});
            }

            return userLoginSuccess({ user: res });
          }),
          tap(res => {
            if (!res['error']) {
              localStorage.setItem('authToken', res['user']['access_token']);
            }
          }),
          catchError(error => {
            return of(userLoginFailure({ error }));
          })
        )
      )
    )
  );


  userSendVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userSendVerification),
      mergeMap(action =>
        this.userService.sendVerification(action.email).pipe(
          map(res => {
            return userSendVerificationSuccess({ user: res });
          }),
          catchError(error => {
            return of(userSendVerificationFailure({ error }));
          })
        )
      )
    )
  );

  resetUserPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userResetPassword),
      mergeMap(action =>
        this.userService.resetPassword(action.password, action.hash, action.email).pipe(
          map(res => {
            return userResetPasswordSuccess({ user: res });
          }),
          catchError(error => {
            return of(userResetPasswordFailure({ error }));
          })
        )
      )
    )
  );

  changeUserPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userchangePassword),
      mergeMap(action =>
        this.userService.changePassword(action.options).pipe(
          map(res => {
            this.pageService.onNotificationToggle({ show: true, error: false, message: 'Password Updated Successfully!' });
            return userchangePasswordSuccess({ passByUser: res });
          }),
          catchError(error => {
            this.pageService.onNotificationToggle({ show: true, error: true, message: 'Something Went Wrong!' });
            return of(userchangePasswordFailure({ error }));
          })
        )
      )
    )
  );

  signupUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userSignup),
      mergeMap(action =>
        this.userService.signup(action.user).pipe(
          map(res => {
            return userSignupSuccess({ user: res });
          }),
          catchError(error => {
            return of(userSignupFailure({ error }));
          })
        )
      )
    )
  );

  resendUser$=createEffect(() =>
    this.actions$.pipe(
      ofType(resendVerification),
      mergeMap(action =>
        this.userService.resend(action.email).pipe(
          map(res => {
            return resendSuccess({ email: res });
          }),
          catchError(error => {
            return of(resendFailure({ error }));
          })
        )
      )
    )
  );
  updateUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUserProfile),
      mergeMap(() => this.userService.updateProfile().pipe(
        map(res => {
          const roleId = res['user'] && res['user']['roles'][0].role_id;

          if (roleId === UserRole.MANAGER || roleId === UserRole.SUPERADMIN) {
            this.userService.logout();
            return updateUserProfileFailure({error: {}});
          }

          return updateUserProfileSuccess({ user: res });
        }),
        catchError(error => {
          return of(updateUserProfileFailure({ error }));
        })
      )
      )
    )
  );

  getUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getUser),
      mergeMap(action =>
        this.userService.getUser(action.id).pipe(
          map(res => {
            return getUserSuccess({ user: res });
          }),
          catchError(error => {
            return of(getUserFailure({ error }));
          })
        )
      )
    )
  );

  getFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getFilters),
      mergeMap(() =>
        this.userService.getFilters().pipe(
          map(res => {
            return getFiltersSuccess({ filters: res });
          }),
          catchError(error => {
            return of(getFiltersFailure({ error }));
          })
        )
      )
    )
  );

  getFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getFilter),
      mergeMap(action =>
        this.userService.getFilter(action.id).pipe(
          map(res => {
            return getFilterSuccess({ filter: res });
          }),
          catchError(error => {
            return of(getFilterFailure({ error }));
          })
        )
      )
    )
  );

  addFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addFilter),
      mergeMap(action =>
        this.userService.insertFilter(action.filter).pipe(
          map(filter => {
            return addFilterSuccess({ filter });
          }),
          tap(res => {
            this.pageService.onNotificationToggle({ show: true, error: false, message: 'Filter Added Successfully!' });
          }),
          catchError(error => {
            this.pageService.onNotificationToggle({ show: true, error: true, message: 'Something Went Wrong!' });
            return of(addFilterFailure({ error }));
          })
        )
      )
    )
  );

  deleteAffiliate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteFilter),
      mergeMap(action =>
        this.userService.removeFilter(action.id).pipe(
          map(() => {
            return deleteFilterSuccess({ id: action.id });
          }),
          catchError(error => {
            return of(deleteFilterFailure({ error }));
          })
        )
      )
    )
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateUser),
      mergeMap(action =>
        this.userService.renewUser(action.user).pipe(
          map((user) => {
            this.pageService.onNotificationToggle({ show: true, error: false, message: 'Profile Updated Successfully!' });
            this.userService.dispatchUpdateProfile();
            return UpdateUserSuccess({ user });
          }),
          catchError(error => {
            this.pageService.onNotificationToggle({ show: true, error: true, message: 'Something Went Wrong!' });
            return of(UpdateUserFailure({ error }));
          })
        )
      )
    )
  );

  getUserLoginHistories$ = createEffect(() =>
  this.actions$.pipe(
    ofType(getUserLoginHistories),
    mergeMap(action =>
      this.userService.getUserLoginHistories(action.userID, action.page, action.filterOptions).pipe(
        map(res => {
          return getUserLoginHistoriesSuccess({ loginHistories: res });
        }),
        catchError(error => {
          // this.sharedService.onLoaderToggle(false);
          return of(getUserLoginHistoriesFailure({ error }));
        })
      )
    )
  )
);

userSendActivation$ = createEffect(() =>
this.actions$.pipe(
  ofType(userSendActivation),
  mergeMap(action =>
    this.userService.sendActivation(action.code, action.email).pipe(
      map(res => {
        const authToken = {
          authToken: res['access_token']
        }
        localStorage.setItem('authToken', res['access_token']);
        return userSendActivationSuccess({ user: authToken });
      }),
      tap(res => this.router.navigate(['promotions', 'campaigns'])),
      catchError(error => {
        return of(userSendActivationFailure({ error }));
      })
    )
  )
)
);

  constructor(
    private actions$: Actions,
    private userService: UserService,
    private pageService: PageService,
    private router: Router
  ) { }
}
